import PropTypes from 'prop-types';
import { IconButton, Tooltip } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { GetAppOutlined } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import {
  downloadFileFromUrl,
  downloadImage,
  isImageFile,
  getFileExtension,
  getAttachmentDownloadUrl,
  getAttachmentDisplayName,
} from 'src/Helpers/FileDownloadUtils';

AttachmentDownloadIconButton.propTypes = {
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  fileIndex: PropTypes.number,
  hidden: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'overlay']),
  sx: PropTypes.object,
};

const isDownloadRestrictedUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userType = user?.isClient ? 3 : user?.user_type;
    return Boolean(user?.isClient || userType === 3);
  } catch {
    return false;
  }
};

export default function AttachmentDownloadIconButton({
  file,
  fileIndex = 0,
  hidden = false,
  variant = 'default',
  sx = {},
}) {
  const { enqueueSnackbar } = useSnackbar();

  if (hidden || isDownloadRestrictedUser()) return null;

  const fileUrl = getAttachmentDownloadUrl(file);
  const fileMeta = typeof file === 'string' ? null : file;
  const displayName = getAttachmentDisplayName(file);

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!fileUrl) {
      enqueueSnackbar('No file to download', { variant: 'warning' });
      return;
    }

    const fileType = fileMeta?.file_type || fileMeta?.type || '';
    const ext = getFileExtension(fileUrl);
    const onSuccess = () =>
      enqueueSnackbar('File downloaded successfully', { variant: 'success' });
    const onError = () =>
      enqueueSnackbar('Failed to download file', { variant: 'error' });

    if (
      isImageFile(fileType) ||
      ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)
    ) {
      downloadImage(fileUrl, displayName, fileIndex, onSuccess, onError);
      return;
    }
    downloadFileFromUrl(fileUrl, displayName, onSuccess, onError);
  };

  const isOverlay = variant === 'overlay';

  return (
    <Tooltip title="Download file">
      <IconButton
        size={isOverlay ? 'medium' : 'small'}
        onClick={handleClick}
        disabled={!fileUrl}
        aria-label="Download file"
        sx={{
          ...(isOverlay
            ? {
                width: 40,
                height: 40,
                p: '8px',
                color: 'common.white',
                bgcolor: (theme) => alpha(theme.palette.common.black, 0.58),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.common.white, 0.45),
                boxShadow: (theme) =>
                  `0 2px 10px ${alpha(theme.palette.common.black, 0.5)}`,
                backdropFilter: 'blur(6px)',
                transition: (theme) =>
                  theme.transitions.create(['background-color', 'transform', 'border-color'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.common.black, 0.78),
                  borderColor: 'common.white',
                  transform: 'scale(1.06)',
                },
              }
            : {
                p: '4px',
                color: 'common.white',
                bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                },
              }),
          ...sx,
        }}
      >
        <GetAppOutlined fontSize={isOverlay ? 'medium' : 'small'} />
      </IconButton>
    </Tooltip>
  );
}
